import React, { useState } from "react";
import "./AccountClosureModal.css";
import { API_URL } from "../../constant";
import { useNavigate } from "react-router-dom";
import { MdWarningAmber, MdCheckCircleOutline } from "react-icons/md";

const REASONS = [
  "No longer needed",
  "Found a better alternative",
  "Privacy concerns (Forgot Me)",
  "Too many emails/notifications",
  "Account security concerns",
  "Others",
];

function AccountClosureModal({ isOpen, onClose }) {
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) {
      setErrorMessage("Please select a reason for closing your account.");
      return;
    }

    if (selectedReason === "Others" && !otherReason.trim()) {
      setErrorMessage("Please provide details for your reason.");
      return;
    }

    if (!hasConfirmed) {
      setErrorMessage("Please acknowledge the account closure conditions.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        navigate("/sign_in");
        return;
      }

      const response = await fetch(`${API_URL}/api/account-closure/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: selectedReason,
          other_reason: selectedReason === "Others" ? otherReason.trim() : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit account closure request.");
      }

      // Success
      setIsSuccess(true);
    } catch (err) {
      setErrorMessage(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = () => {
    // Clear storage and redirect to sign in
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("user");
    sessionStorage.clear();

    onClose();
    navigate("/sign_in");
  };

  return (
    <div className="closure-modal-overlay">
      <div className="closure-modal-box">
        {!isSuccess && (
          <button className="closure-modal-close-btn" onClick={onClose} disabled={isSubmitting}>
            &times;
          </button>
        )}

        {!isSuccess ? (
          <>
            <h3 className="closure-modal-title">Forgot Me – Close My Account</h3>

            {/* Exact Warning Required */}
            <div className="closure-warning-box">
              <div className="closure-warning-header">
                <MdWarningAmber size={20} />
                <span>Account Closure Notice</span>
              </div>
              <p className="closure-warning-text">
                Please note that once you delete your account, you will no longer be able to access your account portal. Your final collected TopCoin balance in your wallet will be calculated, and the outstanding balance will be settled accordingly.
              </p>
            </div>

            {errorMessage && (
              <div style={{ color: "#dc2626", fontSize: "13px", marginBottom: "14px", fontWeight: "600" }}>
                {errorMessage}
              </div>
            )}

            <div className="closure-section-label">Select a reason for closing your account:</div>
            <div className="closure-reason-list">
              {REASONS.map((reason) => (
                <label key={reason} className="closure-reason-item">
                  <input
                    type="radio"
                    name="closureReason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={() => setSelectedReason(reason)}
                  />
                  <span>{reason}</span>
                </label>
              ))}

              {selectedReason === "Others" && (
                <textarea
                  className="closure-textarea"
                  rows="2"
                  placeholder="Please specify your reason..."
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  maxLength={500}
                />
              )}
            </div>

            <label className="closure-confirm-checkbox-box">
              <input
                type="checkbox"
                checked={hasConfirmed}
                onChange={(e) => setHasConfirmed(e.target.checked)}
              />
              <span>
                I understand that my account will be closed, portal access will be permanently disabled, and my outstanding balance will be processed for settlement.
              </span>
            </label>

            <div className="closure-actions">
              <button
                className="closure-btn-submit"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedReason || !hasConfirmed}
              >
                {isSubmitting ? "Submitting Request..." : "Submit Closure Request"}
              </button>
              <button
                className="closure-btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="closure-success-box">
            <MdCheckCircleOutline className="closure-success-icon" />
            <h3 className="closure-success-title">Account Closure Request Submitted</h3>
            <p className="closure-success-desc">
              Your request has been forwarded to administration. Your final collected TopCoin balance will be calculated, and the outstanding balance will be settled accordingly.
              <br />
              <br />
              You have been logged out of your account.
            </p>
            <button className="closure-btn-submit" onClick={handleFinish} style={{ width: "100%" }}>
              Exit Portal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountClosureModal;
